import React, { useState } from 'react';
import {
	ApButtonIcon,
	ApSimpleSearch,
	ApMenuContext,
} from '@alixpartners/ui-components';
import {
	StyledMenu,
	Wrapper,
	SearchWrapper,
	ListItem,
	ListTitle,
	List,
	ListInfo,
} from './SearchMenu.styles';
import { getFormattedSpendWithSymbol } from 'utils/number';

export const SearchMenu = ({
	width,
	offsetRight,
	onSearch,
	currencySymbol,
	options,
	fieldOnly,
	placeholder,
	menuDirection,
}) => {
	const [query, setQuery] = useState('');
	const [cursor, setCursor] = useState(0);
	const onSearchChange = value => {
		setQuery(value);
	};
	const onSearchClear = () => {
		setQuery('');
		setCursor(0);
	};
	const getMatches = () => {
		if (!query) {
			return [];
		}

		return options
			.filter(({ name }) => {
				return name.toLowerCase().includes(query.toLowerCase());
			})
			.sort();
	};
	const getHighlightedName = name => {
		if (!query) {
			return name;
		}

		const index = name.toLowerCase().indexOf(query.toLowerCase());
		const before = name.slice(0, index);
		const searchValue = name.slice(index, index + query.length);
		const after = name.slice(index + query.length, name.length);

		return (
			<React.Fragment>
				{before}
				<span>{searchValue}</span>
				{after}
			</React.Fragment>
		);
	};

	const handleListItemClick = (id, closeMenu) => {
		closeMenu();
		onSearch(id);
	};

	// Show matches starting from 2 characters in search term
	const hasMatches = getMatches().length > 0 && query.length > 1;

	const handleKeyDown = (e, closeMenu) => {
		// Esc, close menu
		if (e.keyCode === 27) {
			closeMenu();

			return;
		}

		if (!hasMatches) {
			return;
		}

		// Arrow up
		if (e.keyCode === 38 && cursor > 0) {
			const newPosition = cursor - 1;
			const el = document.getElementById(`item${newPosition}`);

			setCursor(newPosition);

			// Scroll to selected item if container has scroll
			if (el) {
				el.scrollIntoView({
					behavior: 'smooth',
					block: 'end',
					inline: 'nearest',
				});
			}
			// Arrow down
		} else if (e.keyCode === 40 && cursor < getMatches().length - 1) {
			const newPosition = cursor + 1;
			const el = document.getElementById(`item${newPosition}`);

			setCursor(newPosition);

			// Scroll to selected item if container has scroll
			if (el) {
				el.scrollIntoView({
					behavior: 'smooth',
					block: 'end',
					inline: 'nearest',
				});
			}
			// Enter
		} else if (e.key === 'Enter') {
			handleListItemClick(getMatches()[cursor].id, closeMenu);
		}
	};

	const renderSearchField = closeMenu => {
		closeMenu = closeMenu || onSearchClear;

		return (
			<Wrapper width={width} onKeyDown={e => handleKeyDown(e, closeMenu)}>
				<SearchWrapper>
					<ApSimpleSearch
						autoFocus
						onChange={onSearchChange}
						onClear={onSearchClear}
						placeholder={placeholder}
					/>
				</SearchWrapper>
				{hasMatches && (
					<List direction={menuDirection}>
						{getMatches().map(({ id, name, value }, index) => (
							<ListItem
								key={`${name}-${id}-${value}`}
								className={`${cursor === index ? 'active' : ''}`}
								id={`item${index}`}
								onClick={() => {
									handleListItemClick(id, closeMenu);
								}}
							>
								<ListTitle>{getHighlightedName(name)}</ListTitle>
								<ListInfo>
									{value !== undefined &&
										getFormattedSpendWithSymbol(
											value,
											currencySymbol
										).toUpperCase()}
								</ListInfo>
							</ListItem>
						))}
					</List>
				)}
			</Wrapper>
		);
	};

	if (fieldOnly) {
		return renderSearchField();
	}

	return (
		<StyledMenu
			offsetRight={offsetRight}
			button={<ApButtonIcon iconName="search" />}
			placement="bottom-end"
			onClose={onSearchClear}
		>
			<ApMenuContext.Consumer>
				{({ closeMenu }) => renderSearchField(closeMenu)}
			</ApMenuContext.Consumer>
		</StyledMenu>
	);
};

export default SearchMenu;
